<template>
  <div class="load-analysis-chart-wrapper">
    <div ref="chartDom" class="chart-container"></div>

    <!-- 全屏对话框 -->
    <v-dialog v-model="isFullscreen" :width="dialogWidth" :height="dialogHeight" persistent class="fullscreen-dialog">
      <v-card class="fullscreen-card">
        <!-- 对话框标题栏 -->
        <div class="fullscreen-header">
          <div class="fullscreen-title">负荷分析</div>
          <v-icon icon="mdi-close" class="close-icon" @click="closeFullscreen"></v-icon>
        </div>

        <!-- 放大的内容区域 -->
        <div class="fullscreen-body">
          <LoadAnalysisChartInner :data="data" class="fullscreen-chart-container" />
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick, computed, defineComponent, h } from 'vue'
import { initChart } from '@/utils'
import { generateChartOption, fullscreenDialogConfig } from '@/views/operation/config/loadAnalysisConfig'

const props = defineProps({
  data: {
    type: Object,
    default: () => ({
      categories: [],
      seriesData: [] // 每个电表的数据系列
    })
  }
})

const chartDom = ref(null)
let chartObj = null
let resizeObserver = null

// 全屏状态
const isFullscreen = ref(false)

// 窗口尺寸
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1920)
const windowHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 1080)

const updateWindowSize = () => {
  if (typeof window !== 'undefined') {
    windowWidth.value = window.innerWidth
    windowHeight.value = window.innerHeight
  }
}

// 计算对话框宽度
const dialogWidth = computed(() => {
  const maxWidth = Math.min(windowWidth.value * fullscreenDialogConfig.widthRatio, fullscreenDialogConfig.maxWidth)
  return Math.floor(maxWidth)
})

// 计算对话框高度
const dialogHeight = computed(() => {
  const maxHeight = Math.min(windowHeight.value * fullscreenDialogConfig.heightRatio, fullscreenDialogConfig.maxHeight)
  return Math.floor(maxHeight)
})

const chartOption = computed(() => {
  const { categories, seriesData } = props.data
  return generateChartOption(categories, seriesData)
})

const updateChart = () => {
  if (!chartObj) return
  chartObj.setOption(chartOption.value)
}

const initChartInstance = () => {
  if (!chartDom.value) return
  chartObj = initChart(chartDom.value)
  updateChart()

  // 监听容器大小变化
  resizeObserver = new ResizeObserver(() => {
    chartObj?.resize()
  })
  if (chartDom.value) {
    resizeObserver.observe(chartDom.value)
  }
}

watch(() => chartOption.value, () => {
  updateChart()
}, { deep: true })

// 内部图表组件
const LoadAnalysisChartInner = defineComponent({
  props: {
    data: {
      type: Object,
      default: () => ({
        categories: [],
        seriesData: []
      })
    }
  },
  setup(props) {
    const chartDom = ref(null)
    let chartObj = null
    let resizeObserver = null

    const chartOption = computed(() => {
      const { categories, seriesData } = props.data
      return generateChartOption(categories, seriesData)
    })

    const updateChart = () => {
      if (!chartObj) return
      chartObj.setOption(chartOption.value)
    }

    const initChartInstance = () => {
      if (!chartDom.value) return
      chartObj = initChart(chartDom.value)
      updateChart()

      resizeObserver = new ResizeObserver(() => {
        chartObj?.resize()
      })
      if (chartDom.value) {
        resizeObserver.observe(chartDom.value)
      }
    }

    watch(() => chartOption.value, () => {
      updateChart()
    }, { deep: true })

    watch(() => props.data, () => {
      updateChart()
    }, { deep: true })

    onMounted(() => {
      nextTick(() => {
        initChartInstance()
      })
    })

    onUnmounted(() => {
      if (resizeObserver) {
        resizeObserver.disconnect()
        resizeObserver = null
      }
      if (chartObj) {
        chartObj.dispose()
        chartObj = null
      }
    })

    return () => h('div', { ref: chartDom, class: 'chart-container' })
  }
})

watch(() => props.data, () => {
  updateChart()
}, { deep: true })

// 打开全屏（暴露给父组件调用）
const openFullscreen = () => {
  isFullscreen.value = true
}

// 暴露方法给父组件
defineExpose({
  openFullscreen
})

// 关闭全屏
const closeFullscreen = () => {
  isFullscreen.value = false
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', updateWindowSize)
    updateWindowSize()
  }
  nextTick(() => {
    initChartInstance()
  })
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateWindowSize)
  }
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
  if (chartObj) {
    chartObj.dispose()
    chartObj = null
  }
})
</script>

<style scoped lang="scss">
.load-analysis-chart-wrapper {
  width: 100%;
  height: 100%;
}

.chart-container {
  width: 100%;
  height: 400px;
}

// 全屏对话框样式
:deep(.fullscreen-dialog) {
  .v-overlay__content {
    max-width: 90vw;
    max-height: 90vh;
  }
}

.fullscreen-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
}

.fullscreen-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.63) 0%, rgba(255, 255, 255, 0.42) 104.73%);
  border-bottom: 1px solid rgba(106, 114, 124, 0.1);

  .fullscreen-title {
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 20px;
    font-weight: 400;
    color: #2E2E2E;
  }

  .close-icon {
    cursor: pointer;
    color: rgba(46, 46, 46, 0.70);
    transition: color 0.2s;

    &:hover {
      color: #2E2E2E;
    }
  }
}

.fullscreen-body {
  flex: 1;
  padding: 24px;
  overflow: auto;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.63) 0%, rgba(255, 255, 255, 0.42) 104.73%);
}

.fullscreen-chart-container {
  width: 100%;
  height: 100%;
  min-height: 600px;
}
</style>
